import React, { useState } from 'react';

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { BellIcon, ExclamationTriangleIcon, PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface SendNotificationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  targetUser: {
    id: string;
    name: string;
    surname: string;
    email: string;
  };
}

interface NotificationFormData {
  title: string;
  body: string;
  url: string;
  tag: string;
}

const SendNotificationDialog: React.FC<SendNotificationDialogProps> = ({ 
  isOpen, 
  onClose, 
  targetUser 
}) => {
  const [formData, setFormData] = useState<NotificationFormData>({
    title: '',
    body: '',
    url: '/dashboard',
    tag: 'admin-notification'
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    details?: any;
  } | null>(null);

  const sendAdminNotification = httpsCallable(functions, 'sendAdminNotificationToUser');

  const handleInputChange = (field: keyof NotificationFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSend = async () => {
    if (!formData.title.trim() || !formData.body.trim()) {
      setResult({
        success: false,
        message: 'Title and message are required'
      });
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const response = await sendAdminNotification({
        targetUserId: targetUser.id,
        messageDetails: {
          title: formData.title.trim(),
          body: formData.body.trim(),
          url: formData.url.trim() || '/dashboard',
          tag: formData.tag.trim() || 'admin-notification'
        }
      });

      const data = response.data as any;
      
      setResult({
        success: data.success,
        message: data.message,
        details: data
      });

      if (data.success) {
        // Clear form on success
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error sending notification:', error);
      setResult({
        success: false,
        message: error.message || 'Failed to send notification'
      });
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      body: '',
      url: '/dashboard',
      tag: 'admin-notification'
    });
    setResult(null);
    onClose();
  };

  const predefinedMessages = [
    {
      title: 'Welcome Message',
      body: 'Welcome to Peak! We\'re excited to help you reach your fitness goals.',
      url: '/dashboard'
    },
    {
      title: 'Log Reminder',
      body: 'Don\'t forget to log your daily activities to track your progress.',
      url: '/dashboard'
    },
    {
      title: 'Feature Update',
      body: 'We\'ve added new features to help improve your experience!',
      url: '/updates'
    },
    {
      title: 'Support Message',
      body: 'Our support team is here to help if you have any questions.',
      url: '/support'
    }
  ];

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-2xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-2">
            <BellIcon className="w-6 h-6 text-primary" />
            <h3 className="text-lg font-semibold">Send Push Notification</h3>
          </div>
          <button 
            className="btn btn-sm btn-circle btn-ghost"
            onClick={handleClose}
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Target User Info */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Sending to:</h4>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center text-lg font-semibold">
              {targetUser.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="font-medium">{targetUser.name} {targetUser.surname}</div>
              <div className="text-sm text-gray-600">{targetUser.email}</div>
              <div className="text-xs text-gray-500">User ID: {targetUser.id}</div>
            </div>
          </div>
        </div>

        {/* Quick Templates */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Quick Templates:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {predefinedMessages.map((template, index) => (
              <button
                key={index}
                className="text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                onClick={() => setFormData(prev => ({ ...prev, ...template }))}
              >
                <div className="font-medium text-sm">{template.title}</div>
                <div className="text-xs text-gray-600 line-clamp-2">{template.body}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="space-y-4 mb-6">
          {/* Title */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Title *</span>
              <span className="label-text-alt">{formData.title.length}/100</span>
            </label>
            <input
              type="text"
              placeholder="Enter notification title..."
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              maxLength={100}
              className="input input-bordered w-full"
            />
          </div>

          {/* Body */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Message *</span>
              <span className="label-text-alt">{formData.body.length}/500</span>
            </label>
            <textarea
              placeholder="Enter notification message..."
              value={formData.body}
              onChange={(e) => handleInputChange('body', e.target.value)}
              maxLength={500}
              rows={4}
              className="textarea textarea-bordered w-full"
            />
          </div>

          {/* URL */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Deep Link URL</span>
              <span className="label-text-alt">Optional</span>
            </label>
            <input
              type="text"
              placeholder="/dashboard"
              value={formData.url}
              onChange={(e) => handleInputChange('url', e.target.value)}
              className="input input-bordered w-full"
            />
            <label className="label">
              <span className="label-text-alt">Where users will go when they tap the notification</span>
            </label>
          </div>

          {/* Tag */}
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Notification Tag</span>
              <span className="label-text-alt">Optional</span>
            </label>
            <input
              type="text"
              placeholder="admin-notification"
              value={formData.tag}
              onChange={(e) => handleInputChange('tag', e.target.value)}
              className="input input-bordered w-full"
            />
            <label className="label">
              <span className="label-text-alt">Used to group and replace similar notifications</span>
            </label>
          </div>
        </div>

        {/* Result Display */}
        {result && (
          <div className={`alert mb-6 ${result.success ? 'alert-success' : 'alert-error'}`}>
            {result.success ? (
              <BellIcon className="w-5 h-5" />
            ) : (
              <ExclamationTriangleIcon className="w-5 h-5" />
            )}
            <div>
              <div className="font-medium">{result.message}</div>
              {result.details && result.success && (
                <div className="text-sm">
                  Sent to {result.details.sentToDevices} of {result.details.totalDevices} devices
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="modal-action">
          <button 
            className="btn btn-ghost"
            onClick={handleClose}
            disabled={sending}
          >
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleSend}
            disabled={sending || !formData.title.trim() || !formData.body.trim()}
          >
            {sending ? (
              <>
                <span className="loading loading-spinner loading-sm mr-2"></span>
                Sending...
              </>
            ) : (
              <>
                <PaperAirplaneIcon className="w-4 h-4 mr-2" />
                Send Notification
              </>
            )}
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={handleClose}></div>
    </div>
  );
};

export default SendNotificationDialog; 